import {Component} from "@angular/core";
import {Scilearn} from "../constants";
import {ComponentBase} from "../base/ComponentBase";
import {TranslateService} from "@ngx-translate/core";
import {EventBus} from "../event/EventBus";
import {Timer} from "../services/configuration/timer.service";
import {RecordSelectionEvent} from "../event/ReadAndRecordEvents";
import {MessageService} from "../services/notification/message.service";
import {ModalConfig} from "../services/notification/modal.config";
import {ExitEvent, EXIT_STATUS} from "../event/ExitEvent";
import {MicrophoneCheckEvent, MICROPHONECHECK_STATUS} from "../event/MicrophoneCheckEvent";
import {TimerManagerService} from "../services/configuration/timer.manager.service";
import {TimerStatus} from "../services/configuration/timer.config";
import {ActivityEventData, ActivityModalEvent, MessageEventData} from "../event/ActivityEvent";
import {SessionService} from "../services/configuration/session.service";
import {StudentConfiguration} from "../services/assignment/student.configuration";
import {RaVersion} from "../services/configuration/ra.version";
import {AudioQueueItem, TranslateAudio} from "../services/audio-playback/audioqueue.item";
import {AudioQueueService} from "../services/audio-playback/audioqueue.service";
import {AudioQueueEvent, InterruptAudio} from "../event/AudioQueueEvent";
import {PleaseWaitEvent, PleaseWaitTarget} from "../event/ContentEvents";
import {filter} from "rxjs/operators";
import {BugsnagLoggingService} from "../services/logging/bugsnag-logging.service";

@Component({
    selector: "message-modal",
    templateUrl: "./message.modal.component.html",
    styleUrls: ["./message.modal.component.scss"]
})
export class MessageModalComponent extends ComponentBase {

    private supportModalTypes: Array<string> = [Scilearn.MESSAGE_MODAL, Scilearn.TIMEOUT_MODAL];
    private modalType: string;
    private errorMessageShowing: boolean;
    public activateModal: boolean;
    public timeCount: string;
    public buttonOneObj: any;
    public buttonTwoObj: any;
    public title: string;
    public message: string;
    public category: string;
    public positive: boolean;
    public iconClass: string;
    public timer: Timer;

    constructor(private modalConfig: ModalConfig,
                private eventBus: EventBus,
                private timerManagerService: TimerManagerService,
                private audioQueueService: AudioQueueService,
                private messageService: MessageService,
                private translateService: TranslateService,
                private sessionService: SessionService,
                private studentConfiguration: StudentConfiguration,
                private log: BugsnagLoggingService) {
        super("MessageModalComponent");
        this.subscribeModalObserve();
    }

    private subscribeModalObserve(): void {
        let sub = this.eventBus.pipe(filter(event => event instanceof ActivityModalEvent))
            .subscribe((event: ActivityModalEvent) => {
            let typeSupported = this.supportModalTypes.indexOf(event.data.type) !== -1;

            if (!this.errorMessageShowing && typeSupported && !!event.data.message) {
                this.eventBus.next(new PleaseWaitEvent({show: false, audio: false, destination: PleaseWaitTarget.ANY}));
                this.activateModal = true;
                this.modalType = event.data.type;
                this.setupInactiveTimerObserver(event.data);
                this.setUIProperties(event.data);
            }
        });
        this.subscriptions.push(sub);
    }

    private setupInactiveTimerObserver(data: MessageEventData): void {
        this.timer = data.timer;
        if (!!data.timer && data.timer.getType() === Scilearn._INACTIVE_TIMER) {
            this.timer.timerObservable.subscribe((data) => {
                if (data === TimerStatus.interval && !!this.timer) {
                    this.timeCount = this.timer.showTime();
                }
            });
        } else if (!!data.timer && this.timer.getTimeLeft() > 0) {
            this.timeCount = this.timer.showTime();
        }
    }

    private playMessageAudio(message: any): void {
        if (!!message.audio) {
            this.audioQueueService.reset().subscribe(() => {
                this.eventBus.next(
                    new AudioQueueEvent([new AudioQueueItem(message.audio, TranslateAudio.YES)], InterruptAudio.NO));
            });
        }
    }

    private implementMessageUI(message: any, res: any): void {
        this.title = res[message.text1];
        this.message = res[message.text2];
        this.buttonOneObj = message.button1;
        this.buttonTwoObj = message.button2;
        this.iconClass = message.iconClass;
        this.positive = message.type !== "ERROR";
        this.errorMessageShowing = message.type === "ERROR";
    }

    private determineProperTrainingExitMessage(): void {
        let exitState = "exit_confirm_none";
        let trainingProtocolExists = this.studentConfiguration.protocolDuration > 0;
        if (trainingProtocolExists) {
            exitState = this.timerManagerService.trainingTimeCompleted() ?
                        "exit_confirm_complete" : "exit_confirm_incomplete";
        }
        let message = this.messageService.getMessage(exitState);

        this.translateService.get([message.text1, message.text2]).subscribe((res: any) => {
            this.implementMessageUI(message, res);

            if (trainingProtocolExists && !this.timerManagerService.trainingTimeCompleted()) {
                this.timeCount = this.timerManagerService.getTrainingTimeLeft();
            } else {
                this.timeCount = undefined;
            }

            this.audioQueueService.reset().subscribe(() => {
                this.playMessageAudio(message);
            });
        });
    }

    private setUIProperties(data: ActivityEventData): void {
        if (data.message.match("exit_confirm")) {
            this.determineProperTrainingExitMessage();
        } else {
            let message = this.messageService.getMessage(data.message);
            if (message.type === "ERROR") {
                this.log.error("Error modal: " + data.message);
            }
            this.translateService.get([message.text1, message.text2]).subscribe((res: string) => {
                this.implementMessageUI(message, res);
                this.playMessageAudio(message);
            });
        }
    }

    public timerCountNeeded(): boolean {
        return !!this.timeCount;
    }

    private closeModal(): void {
        this.errorMessageShowing = false;
        this.timeCount = undefined;
        this.activateModal = false;
        this.audioQueueService.hardReset();
        this.modalConfig.removeModal(new ActivityEventData({type: this.modalType, timer: this.timer}));
    }

    public confirmButtonClick(btnClicked: string): void {
        let btnObj = btnClicked === "one" ? this.buttonOneObj : this.buttonTwoObj;

        switch (btnObj.status) {
            case "exit":
                try {
                    let sub = this.sessionService.exit().subscribe(
                        () => this.eventBus.next(new ExitEvent(EXIT_STATUS.ok)),
                        () => this.eventBus.next(new ExitEvent(EXIT_STATUS.error))
                    );
                    this.subscriptions.push(sub);
                } catch (e) {
                    // we may not be able to exit cleanly and close session, in this case just exit
                    this.eventBus.next(new ExitEvent(EXIT_STATUS.ok));
                }
                break;
            case "exit_without_saving":
                this.eventBus.next(new ExitEvent(EXIT_STATUS.ok));
                break;
            case "retry":
                this.sessionService.exit().subscribe(() => {
                    window.location.reload();
                }, () => this.eventBus.next(new ExitEvent(EXIT_STATUS.error)));
                break;
            case "record":
                this.eventBus.next(new RecordSelectionEvent());
                this.closeModal();
                break;
            case "cancel_miccheck":
                this.closeModal();
                this.eventBus.next(new MicrophoneCheckEvent(MICROPHONECHECK_STATUS.cancel)); // needed for word wall
                break;
            default:
                this.closeModal();
                break;
        }
    }

    public getBorderState(): {"positive": boolean, "negative": boolean} {
        return {
            "positive": this.positive,
            "negative": !this.positive
        };
    }

    public getIconClass(): Object {
        let iconClassObj = {};
        iconClassObj[this.iconClass] = true;
        return iconClassObj;
    }

}
